import { usablePlaceCoord } from '../../lib/geocode'
import type { LatLon } from '../../lib/types'

/**
 * Was aus zwei Zahlenfeldern wird, in denen jemand gerade tippt.
 *
 * Bewusst kein React in dieser Datei. Die Entscheidung „was davon geht in die
 * Reise“ ist der Kern der Sache und lässt sich so ohne DOM prüfen; die
 * Oberfläche daneben hält nur die Felder und zeigt die Meldung an. Dieselbe
 * Aufteilung wie bei stageRouting.ts.
 */

/** Der Inhalt der beiden Felder, so wie er dasteht. */
export interface KoordinatenEntwurf {
  lat: string
  lon: string
}

/** Was mit dem Entwurf zu geschehen hat. */
export type KoordinatenBefund =
  /** Beide Felder leer: die Koordinate fällt weg. */
  | { art: 'entfernen' }
  /** Beide Felder tragen einen Ort auf der Erde: der wird gesetzt. */
  | { art: 'setzen'; at: LatLon }
  /** Ein Feld leer: jemand ist mitten im Tippen, es bleibt beim Bisherigen. */
  | { art: 'unvollstaendig' }
  /** Beide Felder gefüllt, aber das ist kein Ort: es bleibt beim Bisherigen. */
  | { art: 'unbrauchbar' }

/**
 * Prüft den Entwurf.
 *
 * Geschrieben wird nur bei 'setzen' und 'entfernen'. Alles andere lässt den
 * Bestand, wie er ist - denn eine halbe Koordinate ist keine: „46.36“ allein
 * ist nach dem ersten Tastendruck erst eine 4, und ein geleertes Feld wäre über
 * Number('') eine 0 und damit der Äquator. Beides stünde danach als Tatsache im
 * Dokument, ginge durch den Abgleich und landete im Navigationsgerät.
 */
export function pruefeEntwurf(entwurf: KoordinatenEntwurf): KoordinatenBefund {
  const lat = entwurf.lat.trim()
  const lon = entwurf.lon.trim()
  if (lat === '' && lon === '') return { art: 'entfernen' }
  if (lat === '' || lon === '') return { art: 'unvollstaendig' }

  const breite = Number(lat)
  const laenge = Number(lon)
  if (!usablePlaceCoord(breite, laenge)) return { art: 'unbrauchbar' }
  return { art: 'setzen', at: [breite, laenge] }
}

/**
 * Der Schlüssel einer gespeicherten Koordinate, für den Vergleich mit dem
 * Entwurf.
 *
 * Verglichen wird der Wert und nicht die Liste: eine Koordinate, die über den
 * Abgleich hereinkommt, ist ein neues Array mit denselben Zahlen. Ohne Wert
 * wird der Schlüssel leer - „keine Koordinate“ hat auch eine Schreibweise.
 */
export function koordinatenSchluessel(at: LatLon | undefined): string {
  return at ? `${String(at[0])},${String(at[1])}` : ''
}

/** Die beiden Feldtexte zu einem solchen Schlüssel. */
export function entwurfAus(schluessel: string): KoordinatenEntwurf {
  if (schluessel === '') return { lat: '', lon: '' }
  const [lat = '', lon = ''] = schluessel.split(',')
  return { lat, lon }
}
